import React, {
    useEffect,
    useState,
} from 'react';
import liff, { Liff } from '@line/liff';
import { Profile } from '@liff/get-profile';

export type LiffProviderValue = {
    liff: Liff,
    liffId: string,
    profile: Profile | null,
    isReady: boolean,
    isLoggedIn: boolean,
    isInClient: boolean,
    onlyInClient: boolean,
    error: string | null,
    state: LiffState,
    isLoadLiff: () => boolean,
};

export enum LiffState {
    'Uninitialized' = 'Uninitialized',
    'Ready' = 'Ready',
    'LoggedIn' = 'LoggedIn',
    'ProfileLoaded' = 'ProfileLoaded',
    'Error' = 'Error',
}

export const LiffContext: React.Context<LiffProviderValue> = React.createContext({
    profile: null,
    isLoggedIn: false,
} as LiffProviderValue);

interface LiffProviderProps {
    onlyInClient?: boolean,
    liffId: string,
    children: React.ReactNode,
}

LiffProvider.defaultProps = {
    onlyInClient: false,
};

export function LiffProvider(props: LiffProviderProps): React.ReactElement {
    const {
        onlyInClient = LiffProvider.defaultProps.onlyInClient,
        liffId,
        children,
    } = props;
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isReady, setReady] = useState(false);
    const [isInClient, setIsInClient] = useState(false);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [state, setState] = useState<LiffState>(LiffState.Uninitialized);
    const [error, setError] = useState<string | null>('');

    useEffect(() => {
        console.info('[liff state]', state);
        if (state === LiffState.ProfileLoaded) {
            console.info('[liff profile]', profile);
        }
    }, [state]);

    useEffect(() => {
        setIsInClient(liff.isInClient());

        if (onlyInClient && !liff.isInClient()) {
            return;
        }

        liff.init({
            liffId,
        })
            .then(() => {
                setReady(true);
                setState(LiffState.Ready);
                setIsLoggedIn(liff.isLoggedIn());

                if (!liff.isLoggedIn()) {
                    liff.login({ redirectUri: window.location.href });
                }

                if (liff.isLoggedIn()) {
                    setState(LiffState.LoggedIn);
                    liff.getProfile().then((resProfile) => {
                        setProfile(resProfile);
                        setState(LiffState.ProfileLoaded);
                    }).catch((e) => {
                        setState(LiffState.Error);
                        setError(e.message);
                    });
                }
            })
            .catch((e) => {
                setState(LiffState.Error);
                setError(e.message);
                if (liff.isInClient()) {
                    alert(`${e.code} ${e.message}`);
                } else {
                    console.log(e.code, e.message);
                }
            });
    }, []);

    function isLoadLiff() {
        return (!onlyInClient || (onlyInClient && isInClient));
    }

    return (
        <LiffContext.Provider
            value={{
                liff,
                liffId,
                profile,
                isReady,
                isLoggedIn,
                isInClient,
                onlyInClient,
                state,
                error,
                isLoadLiff,
            }}
        >
            {children}
        </LiffContext.Provider>
    );
}
